import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { INSMSession } from './insm-session';
import { AnimationFPSIM } from './period-measurers/afps';
import { INPTracker } from './inp-measurers/inp';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
export var INSM = /*#__PURE__*/function () {
  function INSM(options) {
    var _this = this;
    _classCallCheck(this, INSM);
    /**
     * Heavy tasks are tracked at the insm layer as heavy tasks
     * are expected at times to be unrelated to the current
     * page session.
     */
    _defineProperty(this, "runningHeavyTasks", new Set());
    this.periodMeasurers = [new AnimationFPSIM(), new INPTracker()];
    this.options = options;

    // If this does throw -- we do want an unhandledRejection rejection to be passed to the window
    // this is to ease debugging.
    options.getAnalyticsWebClient.then(function (analyticsWebClient) {
      return _this.analyticsWebClient = analyticsWebClient;
    });

    // No cleanup needs to be performed -- as this tooling is intended to run until the tab is closed
    // The use of beforeunload here is because using pagehide does not reliably result in the analytics
    // being fired by the analytics web client.
    // The use of this means we will miss events from mobile safari which does not reliably call this api
    // however mobile browsers do not call when a browser closes - so we expect limited mobile data from
    // this tooling in its current form.

    // window will not be defined in server envs - in these envs -- there can never be a session end
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
      window.addEventListener('beforeunload', function () {
        var _this$runningSession;
        (_this$runningSession = _this.runningSession) === null || _this$runningSession === void 0 || _this$runningSession.end({
          stoppedBy: 'beforeunload'
        });
      });
    }
  }

  /**
   * Starts a heavy task in the currently running session.
   *
   * This also pauses measurement.
   *
   * For PageLoads using the key 'PageLoad' will mean the heavy task duration
   * is added to the insm session event as pageLoadTime.
   */
  return _createClass(INSM, [{
    key: "startHeavyTask",
    value: function startHeavyTask(heavyTaskName) {
      var _this$runningSession2, _this$runningSession3, _this$runningSession4;
      this.runningHeavyTasks.add(heavyTaskName);
      (_this$runningSession2 = this.runningSession) === null || _this$runningSession2 === void 0 || (_this$runningSession2 = _this$runningSession2.periodTracking) === null || _this$runningSession2 === void 0 || _this$runningSession2.startHeavyTask(heavyTaskName);
      (_this$runningSession3 = this.runningSession) === null || _this$runningSession3 === void 0 || _this$runningSession3.periodTracking.pause(heavyTaskName);
      (_this$runningSession4 = this.runningSession) === null || _this$runningSession4 === void 0 || _this$runningSession4.longAnimationFrameMeasurer.pause();
    }

    /**
     * Ends a heavy task in the currently running session
     */
  }, {
    key: "endHeavyTask",
    value: function endHeavyTask(heavyTaskName) {
      var _this$runningSession5;
      this.runningHeavyTasks.delete(heavyTaskName);
      (_this$runningSession5 = this.runningSession) === null || _this$runningSession5 === void 0 || _this$runningSession5.periodTracking.resume(heavyTaskName);
      if (this.runningHeavyTasks.size === 0) {
        var _this$runningSession6;
        (_this$runningSession6 = this.runningSession) === null || _this$runningSession6 === void 0 || _this$runningSession6.longAnimationFrameMeasurer.resume();
      }
      if (expValEquals('cc_editor_insm_outlier_events', 'cohort', 'test')) {
        if (heavyTaskName === 'PageLoad') {
          var _this$runningSession7;
          (_this$runningSession7 = this.runningSession) === null || _this$runningSession7 === void 0 || _this$runningSession7.completePageLoad();
        }
      }
    }

    /**
     * Call this when starting a new experience.  This is expected to be wired to the product
     * routing solution.
     *
     * It's expected this call will be paired with a `insm.session.startHeavyTask('PageLoad')` and
     * subsequent `insm.session.endHeavyTask('PageLoad')` so that performance degradations linked
     * to the page initialisation are excluded from the active interactivity monitoring.
     *
     * Using the key 'PageLoad' is special and will result in the heavy task duration being added to the
     * insm session event as pageLoadTime.
     *
     *
     * ```ts
     * insm.start('edit-page', { initial: true, contentId: '9001' })
     * insm.session.startHeavyTask('PageLoad')
     * // ... heavy initialisation work
     * insm.session.endHeavyTask('PageLoad')
     * ```
     */
  }, {
    key: "start",
    value: function start(experienceKey, experienceProperties) {
      var _this$options$experie;
      if (this.runningSession !== undefined) {
        this.runningSession.end({
          stoppedBy: 'new-experience',
          experienceKey: experienceKey,
          contentId: experienceProperties.contentId
        });
      }
      this.lastStartedExperienceProperties = experienceProperties;
      if ((_this$options$experie = this.options.experiences[experienceKey]) !== null && _this$options$experie !== void 0 && _this$options$experie.enabled) {
        this.runningSession = new INSMSession(experienceKey, experienceProperties, this);
      }
    }
  }, {
    key: "overrideExperienceKey",
    value:
    /**
     * Call this to update the name of the running session after it's started
     * In the case it's been started with an unregistered name, and there is not running
     * session. This will also trigger the session being started.
     */
    function overrideExperienceKey(experienceKey) {
      if (this.runningSession !== undefined) {
        // If there is a running session - we update its name
        this.runningSession.updateExperienceKey(experienceKey);
      } else {
        var _this$options$experie2;
        // otherwise - we assume the last session was not registered, and start it with the new name
        if ((_this$options$experie2 = this.options.experiences[experienceKey]) !== null && _this$options$experie2 !== void 0 && _this$options$experie2.enabled && this.lastStartedExperienceProperties) {
          this.runningSession = new INSMSession(experienceKey, this.lastStartedExperienceProperties, this);
        }
      }
    }

    /**
     * This prematurely halts any running experience measurement. It's expected to be used in
     * scenarios such as when error boundaries are hit.
     */
  }, {
    key: "stopEarly",
    value: function stopEarly(reasonKey, description) {
      var _this$runningSession8;
      (_this$runningSession8 = this.runningSession) === null || _this$runningSession8 === void 0 || _this$runningSession8.earlyStop(reasonKey, description);
    }

    /**
     * Gets the current running session details
     */
  }, {
    key: "session",
    get: function get() {
      return this.runningSession;
    }
  }]);
}();